import React, { useState, useEffect, useRef } from 'react';
import { useToast } from '../../../components/Toast';
import './ResultStep.css';

// 12가지 학생 페르소나 정의
const studentPersonas = {
  athletic_star: {
    id: "athletic_star",
    name: "운동장 인기 스타",
    subtitle: "활발하고 친화력 좋은 학생",
    keywords: ["활발함", "친화력", "리더십", "열정"],
    color: "#FF6B9D"
  },
  quiet_reader: {
    id: "quiet_reader",
    name: "조용한 독서왕",
    subtitle: "차분하고 깊이 있게 사고하는 학생",
    keywords: ["집중력", "사색", "지식", "차분함"],
    color: "#74B9FF"
  },
  creative_artist: {
    id: "creative_artist",
    name: "창의적 예술가",
    subtitle: "창의성과 예술적 감각이 뛰어난 학생",
    keywords: ["창의성", "예술감각", "표현력", "상상력"],
    color: "#A29BFE"
  },
  leadership_leader: {
    id: "leadership_leader",
    name: "리더십 반장님",
    subtitle: "책임감 있고 리더십이 강한 학생",
    keywords: ["리더십", "책임감", "조직력", "신뢰"],
    color: "#6C5CE7"
  },
  kind_helper: {
    id: "kind_helper",
    name: "친절한 도우미",
    subtitle: "배려심 많고 타인을 잘 돕는 학생",
    keywords: ["친절", "배려", "공감", "협력"],
    color: "#4ECDC4"
  },
  curious_scientist: {
    id: "curious_scientist",
    name: "호기심 과학자",
    subtitle: "탐구심 많고 논리적인 학생",
    keywords: ["탐구심", "논리성", "분석력", "호기심"],
    color: "#00B894"
  },
  persistent_achiever: {
    id: "persistent_achiever",
    name: "끈기있는 노력가",
    subtitle: "성실하고 꾸준히 노력하는 학생",
    keywords: ["끈기", "성실", "노력", "인내"],
    color: "#45B7D1"
  },
  cheerful_mood: {
    id: "cheerful_mood",
    name: "유쾌한 분위기 메이커",
    subtitle: "밝고 긍정적인 에너지를 주는 학생",
    keywords: ["유쾌함", "긍정", "활력", "친근함"],
    color: "#FDCB6E"
  },
  careful_planner: {
    id: "careful_planner",
    name: "신중한 계획가",
    subtitle: "체계적이고 계획적인 학생",
    keywords: ["계획성", "체계성", "신중함", "준비"],
    color: "#9B59B6"
  },
  empathetic_soul: {
    id: "empathetic_soul",
    name: "공감능력자",
    subtitle: "감정 이해도가 높고 공감을 잘하는 학생",
    keywords: ["공감", "이해", "위로", "감성"],
    color: "#FD79A8"
  },
  brave_adventurer: {
    id: "brave_adventurer",
    name: "도전하는 모험가",
    subtitle: "새로운 것에 도전하는 용기 있는 학생",
    keywords: ["도전", "용기", "모험", "적극성"],
    color: "#E67E22"
  },
  harmonious_mediator: {
    id: "harmonious_mediator",
    name: "조화로운 중재자",
    subtitle: "균형감 있고 갈등을 잘 조율하는 학생",
    keywords: ["조화", "균형", "중재", "포용"],
    color: "#FF7675"
  }
};

// 설문 결과를 기반으로 페르소나 결정
const determinePersonaFromSurvey = (surveyData) => {
  if (!surveyData || Object.keys(surveyData).length === 0) {
    return studentPersonas.athletic_star;
  }

  const answers = surveyData;
  
  // 1-7 값을 0-100 스케일로 변환
  const convertTo100Scale = (value) => {
    if (value === null || value === undefined) return 50;
    return ((value - 1) / 6) * 100;
  };
  
  // 각 특성별 점수 계산
  const activeness = (
    convertTo100Scale(answers[1]?.value) + // 활발한 활동
    convertTo100Scale(answers[6]?.value)   // 함께 활동
  ) / 20; // 0-10 스케일
  
  const independence = (
    (100 - convertTo100Scale(answers[2]?.value)) + // 독립적 해결
    convertTo100Scale(answers[6]?.value) === 0 ? 100 : (100 - convertTo100Scale(answers[6]?.value)) // 혼자 활동
  ) / 20;
  
  const creativity = (
    convertTo100Scale(answers[3]?.value) + // 즉각적 도전
    (100 - convertTo100Scale(answers[5]?.value)) // 창의 및 실행
  ) / 20;
  
  const leadership = (
    convertTo100Scale(answers[2]?.value) + // 적극적 질문
    (100 - convertTo100Scale(answers[5]?.value)) // 창의 및 실행
  ) / 20;
  
  const kindness = (
    convertTo100Scale(answers[4]?.value) + // 공감적 소통
    convertTo100Scale(answers[6]?.value)   // 함께 활동
  ) / 20;
  
  const curiosity = (
    convertTo100Scale(answers[2]?.value) + // 적극적 질문
    convertTo100Scale(answers[3]?.value)   // 즉각적 도전
  ) / 20;
  
  const persistence = (
    (100 - convertTo100Scale(answers[7]?.value)) + // 계획적 접근
    (100 - convertTo100Scale(answers[3]?.value))   // 신중한 접근
  ) / 20;
  
  const cheerfulness = (
    convertTo100Scale(answers[1]?.value) + // 활발한 활동
    convertTo100Scale(answers[3]?.value)   // 즉각적 도전
  ) / 20;
  
  const planning = (
    (100 - convertTo100Scale(answers[7]?.value)) + // 계획적 접근
    convertTo100Scale(answers[5]?.value)   // 계획 및 조직
  ) / 20;
  
  const empathy = convertTo100Scale(answers[4]?.value) / 10; // 공감적 소통
  
  const bravery = (
    convertTo100Scale(answers[3]?.value) + // 즉각적 도전
    convertTo100Scale(answers[7]?.value)   // 유연한 접근
  ) / 20;
  
  const harmony = (
    (50 + (50 - Math.abs(convertTo100Scale(answers[1]?.value) - 50))) + // 중간 성향
    convertTo100Scale(answers[4]?.value)   // 공감적 소통
  ) / 20;

  // 12개 페르소나별 점수 계산
  const scores = {};
  
  scores.athletic_star = activeness * 0.4 + leadership * 0.3 + cheerfulness * 0.3;
  scores.quiet_reader = independence * 0.5 + persistence * 0.3 + (10 - activeness) * 0.2;
  scores.creative_artist = creativity * 0.5 + independence * 0.3 + empathy * 0.2;
  scores.leadership_leader = leadership * 0.5 + planning * 0.3 + activeness * 0.2;
  scores.kind_helper = kindness * 0.5 + empathy * 0.3 + harmony * 0.2;
  scores.curious_scientist = curiosity * 0.5 + independence * 0.3 + persistence * 0.2;
  scores.persistent_achiever = persistence * 0.5 + planning * 0.3 + (10 - bravery) * 0.2;
  scores.cheerful_mood = cheerfulness * 0.5 + activeness * 0.3 + kindness * 0.2;
  scores.careful_planner = planning * 0.5 + persistence * 0.3 + (10 - bravery) * 0.2;
  scores.empathetic_soul = empathy * 0.5 + kindness * 0.4 + harmony * 0.1;
  scores.brave_adventurer = bravery * 0.5 + curiosity * 0.3 + activeness * 0.2;
  scores.harmonious_mediator = harmony * 0.4 + empathy * 0.3 + kindness * 0.3;

  // 가장 높은 점수의 페르소나 선택
  let bestPersona = 'athletic_star';
  let bestScore = -1;

  Object.entries(scores).forEach(([personaId, score]) => {
    if (score > bestScore) {
      bestPersona = personaId;
      bestScore = score;
    }
  });

  return studentPersonas[bestPersona] || studentPersonas.athletic_star;
};

const ResultStep = ({ result, surveyData, onRestart }) => {
  const { showToast } = useToast();
  const [currentView, setCurrentView] = useState('persona'); // persona, emphasis, generating, final
  const [selectedEmphasis, setSelectedEmphasis] = useState([]);
  const [generatedSaenggibu, setGeneratedSaenggibu] = useState('');

  const canvasRef = useRef(null);
  const [finalImage, setFinalImage] = useState(null);

  const persona = result?.persona || determinePersonaFromSurvey(surveyData);

  // 컴포넌트 마운트 시 바로 Canvas 이미지 생성
  useEffect(() => {
    const generateImage = async () => {
      if (currentView === 'final' && generatedSaenggibu && !finalImage) {
        const canvas = canvasRef.current;
        if (!canvas) return;
        
        const ctx = canvas.getContext('2d');
        
        // Canvas 크기 설정 (A4 비율)
        const width = 2100;
        const height = 2970;
        canvas.width = width;
        canvas.height = height;
        
        // 배경을 흰색으로 설정
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
        
        // PNG 이미지 로드 및 그리기
        const img = new Image();
        img.crossOrigin = 'anonymous';
        
        await new Promise((resolve) => {
          img.onload = () => {
            ctx.drawImage(img, 0, 0, width, height);
            
            // 페르소나 텍스트들
            ctx.font = 'bold 60px Pretendard, -apple-system, sans-serif';
            ctx.fillStyle = '#000000';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            
            const personaTraits = getPersonaTraits(persona);
            
            // 4개 특성 박스
            ctx.fillText(personaTraits.trait1 || '친화력', width * 0.19, height * 0.215);
            ctx.fillText(personaTraits.trait2 || '활발함', width * 0.81, height * 0.215);
            ctx.fillText(personaTraits.trait3 || '리더십', width * 0.19, height * 0.42);
            ctx.fillText(personaTraits.trait4 || '열정적', width * 0.81, height * 0.42);
            
            // 생기부 텍스트
            ctx.font = '45px Pretendard, -apple-system, sans-serif';
            ctx.fillStyle = '#000000';
            ctx.textAlign = 'left';
            ctx.textBaseline = 'top';
            const textX = width * 0.08;
            const textY = height * 0.59;
            const textWidth = width * 0.84;
            const lineHeight = 50;
            const maxCharsPerLine = Math.floor(textWidth / 33);
            
            const text = generatedSaenggibu || '학생 생기부가 생성 중입니다...';
            const lines = [];
            let currentLine = '';
            
            for (let i = 0; i < text.length; i++) {
              currentLine += text[i];
              if (currentLine.length >= maxCharsPerLine || i === text.length - 1) {
                lines.push(currentLine);
                currentLine = '';
              }
            }
            
            lines.forEach((line, index) => {
              ctx.fillText(line, textX, textY + (index * lineHeight));
            });
            
            const dataURL = canvas.toDataURL('image/png', 1.0);
            setFinalImage(dataURL);
            resolve(dataURL);
          };
          
          const imageNumber = getPersonaImageNumber(persona);
          img.src = `/images/${imageNumber}.png`;
        });
      }
    };
    generateImage();
  }, [currentView, generatedSaenggibu, finalImage, persona]);
  
  // 강조 포인트 옵션들
  const emphasisOptions = [
    { id: 'academic_excellence', label: '학습 우수성', description: '뛰어난 학업 성취도와 학습 능력' },
    { id: 'creativity_arts', label: '창의성/예술', description: '창의적 사고력과 예술적 감각' },
    { id: 'leadership_social', label: '리더십/사회성', description: '리더십과 원만한 대인관계' },
    { id: 'communication', label: '소통/발표', description: '의사소통 능력과 발표력' },
    { id: 'problem_solving', label: '문제해결력', description: '논리적 사고와 문제 해결 능력' },
    { id: 'collaboration', label: '협업/배려', description: '협동심과 타인에 대한 배려' },
    { id: 'responsibility', label: '책임감/성실성', description: '강한 책임감과 성실한 태도' },
    { id: 'passion_interest', label: '열정/관심', description: '특정 분야에 대한 열정과 관심' },
    { id: 'growth_potential', label: '성장 가능성', description: '지속적인 발전과 성장 의지' },
    { id: 'empathy_care', label: '공감/돌봄', description: '타인에 대한 공감과 돌봄' },
    { id: 'innovation', label: '혁신/도전', description: '새로운 시도와 도전 정신' },
    { id: 'organization', label: '체계성/계획성', description: '체계적이고 계획적인 접근' },
    { id: 'adaptability', label: '적응력/유연성', description: '변화에 대한 적응력과 유연성' },
    { id: 'global_perspective', label: '글로벌/다문화', description: '글로벌 마인드와 다문화 이해' },
    { id: 'technology', label: '기술/디지털', description: '디지털 리터러시와 기술 활용' }
  ];

  const handleEmphasisToggle = (emphasisId) => {
    setSelectedEmphasis(prev => {
      if (prev.includes(emphasisId)) {
        return prev.filter(id => id !== emphasisId);
      } else if (prev.length < 5) {
        return [...prev, emphasisId];
      }
      return prev;
    });
  };

  const generateSaenggibu = async () => {
    if (selectedEmphasis.length === 0) {
      showToast('강조하고 싶은 포인트를 1개 이상 선택해주세요', 'warning');
      return;
    }

    setCurrentView('generating');

    try {
      const requestData = {
        persona: persona,
        surveyAnswers: surveyData,
        emphasisPoints: selectedEmphasis,
        targetLength: 300,
        style: 'student_profile'
      };

      const result = await generateStudentSaenggibu(requestData);
      
      if (result.success) {
        setGeneratedSaenggibu(result.content);
        setCurrentView('final');
      } else {
        throw new Error(result.error || '생성 중 오류가 발생했습니다.');
      }

    } catch (error) {
      console.error('생기부 생성 실패:', error);
      showToast('생기부 문구를 만들지 못했어요. 다시 시도해주세요', 'error');
      setCurrentView('emphasis');
    }
  };

  // 실제 생기부 API 호출 함수
  const generateStudentSaenggibu = async (requestData) => {
    try {
      const surveyContext = generateSurveyContext(requestData.surveyAnswers);

      let API_BASE_URL;
      if (process.env.NODE_ENV === 'development') {
        API_BASE_URL = 'http://localhost:8000';
      } else if (window.location.hostname.includes('whatquiztest')) {
        API_BASE_URL = 'https://quiz-api-test-397277209875.us-central1.run.app';
      } else {
        API_BASE_URL = 'https://quiz-api-397277209875.us-central1.run.app';
      }

      const apiRequestData = {
        persona_name: requestData.persona.name,
        persona_description: requestData.persona.description || requestData.persona.subtitle,
        survey_characteristics: surveyContext || ['학생으로서의 기본 소양을 갖춘 학습자'],
        emphasis_points: requestData.emphasisPoints,
        target_length: requestData.targetLength || 300
      };

      const response = await fetch(`${API_BASE_URL}/api/student-profile/generate-saenggibu`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(apiRequestData)
      });

      if (!response.ok) {
        throw new Error(`API 오류: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        return { success: true, content: result.content };
      } else {
        throw new Error(result.error || 'API 응답 오류');
      }
      
    } catch (error) {
      console.error('생기부 API 호출 오류:', error);
      return { 
        success: false, 
        error: '생기부 생성 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.' 
      };
    }
  };

  // 설문 데이터를 기반으로 학생 특성 컨텍스트 생성
  const generateSurveyContext = (surveyData) => {
    if (!surveyData || Object.keys(surveyData).length === 0) return '';
    
    const contexts = [];
    const answers = surveyData;
    
    // 질문 1: 조용한 활동 vs 활발한 활동
    if (answers[1]?.value <= 3) {
      contexts.push('쉬는 시간에 조용히 책을 읽거나 그림을 그리며 차분하게 시간을 보내는 학생');
    } else if (answers[1]?.value >= 5) {
      contexts.push('쉬는 시간에 친구들과 활발하게 어울리며 에너지 넘치는 활동을 즐기는 학생');
    }
    
    // 질문 2: 독립적 해결 vs 적극적 질문
    if (answers[2]?.value <= 3) {
      contexts.push('수업 중 모르는 내용이 있을 때 스스로 생각하고 해결하려고 노력하는 학생');
    } else if (answers[2]?.value >= 5) {
      contexts.push('수업 중 궁금한 점을 적극적으로 질문하고 토론에 참여하는 학생');
    }
    
    // 질문 3: 신중한 접근 vs 즉각적 도전
    if (answers[3]?.value <= 3) {
      contexts.push('새로운 활동이나 과제를 접했을 때 신중하게 관찰하고 준비하는 학생');
    } else if (answers[3]?.value >= 5) {
      contexts.push('새로운 활동이나 과제에 두려움 없이 바로 도전하고 시도하는 학생');
    }
    
    // 질문 4: 논리적 설득 vs 공감적 소통
    if (answers[4]?.value <= 3) {
      contexts.push('친구와 의견이 다를 때 논리적으로 설명하며 합리적으로 문제를 해결하는 학생');
    } else if (answers[4]?.value >= 5) {
      contexts.push('친구와 의견이 다를 때 상대방의 감정을 이해하고 공감하며 대화로 해결하는 학생');
    }
    
    // 질문 5: 계획 및 조직 vs 창의 및 실행
    if (answers[5]?.value <= 3) {
      contexts.push('모둠 활동에서 체계적으로 계획을 세우고 정리하는 역할을 담당하는 학생');
    } else if (answers[5]?.value >= 5) {
      contexts.push('모둠 활동에서 창의적인 아이디어를 제안하고 실행하는 역할을 담당하는 학생');
    }
    
    // 질문 6: 혼자 활동 vs 함께 활동
    if (answers[6]?.value <= 3) {
      contexts.push('자유시간에 조용히 혼자 집중하며 깊이 있는 활동을 선호하는 학생');
    } else if (answers[6]?.value >= 5) {
      contexts.push('자유시간에 친구들과 함께 어울리며 협력적인 활동을 즐기는 학생');
    }
    
    // 질문 7: 계획적 접근 vs 유연한 접근
    if (answers[7]?.value <= 3) {
      contexts.push('목표를 달성하기 위해 차근차근 계획을 세우고 단계적으로 실행하는 학생');
    } else if (answers[7]?.value >= 5) {
      contexts.push('목표를 달성하기 위해 상황에 맞춰 즉흥적이고 유연하게 대처하는 학생');
    }
    
    return contexts;
  };

  // 페르소나별 이미지 번호 매핑
  const getPersonaImageNumber = (persona) => {
    const personaImageMap = {
      'athletic_star': '001',
      'quiet_reader': '002',
      'creative_artist': '003',
      'leadership_leader': '004',
      'kind_helper': '005',
      'curious_scientist': '006',
      'persistent_achiever': '007',
      'cheerful_mood': '008',
      'careful_planner': '009',
      'empathetic_soul': '010',
      'brave_adventurer': '011',
      'harmonious_mediator': '012'
    };
    
    return personaImageMap[persona?.id] || '001';
  };

  const getPersonaTraits = (persona) => {
    // 12개 페르소나별 특성 매핑 (Canvas 표시용 - 4개 특성)
    const personaTraitsMap = {
      'athletic_star': {
        trait1: '활발함',
        trait2: '친화력',
        trait3: '리더십',
        trait4: '열정적'
      },
      'quiet_reader': {
        trait1: '집중력',
        trait2: '사색적',
        trait3: '지식 추구',
        trait4: '차분함'
      },
      'creative_artist': {
        trait1: '창의성',
        trait2: '예술 감각',
        trait3: '표현력',
        trait4: '상상력'
      },
      'leadership_leader': {
        trait1: '리더십',
        trait2: '책임감',
        trait3: '조직력',
        trait4: '신뢰감'
      },
      'kind_helper': {
        trait1: '친절함',
        trait2: '배려심',
        trait3: '공감능력',
        trait4: '협력적'
      },
      'curious_scientist': {
        trait1: '탐구심',
        trait2: '논리성',
        trait3: '분석력',
        trait4: '호기심'
      },
      'persistent_achiever': {
        trait1: '끈기',
        trait2: '성실함',
        trait3: '노력형',
        trait4: '인내심'
      },
      'cheerful_mood': {
        trait1: '유쾌함',
        trait2: '긍정적',
        trait3: '활력',
        trait4: '친근함'
      },
      'careful_planner': {
        trait1: '계획성',
        trait2: '체계적',
        trait3: '신중함',
        trait4: '준비성'
      },
      'empathetic_soul': {
        trait1: '공감력',
        trait2: '이해심',
        trait3: '위로',
        trait4: '감성적'
      },
      'brave_adventurer': {
        trait1: '도전정신',
        trait2: '용기',
        trait3: '모험심',
        trait4: '적극성'
      },
      'harmonious_mediator': {
        trait1: '조화',
        trait2: '균형감',
        trait3: '중재력',
        trait4: '포용력'
      }
    };

    const defaultTraits = {
      trait1: '친화력',
      trait2: '활발함',
      trait3: '리더십',
      trait4: '열정적'
    };

    return personaTraitsMap[persona.id] || defaultTraits;
  };

  const handlePrint = async () => {
    if (!finalImage) {
      showToast('이미지를 아직 만들지 못했어요. 잠시 후 다시 시도해주세요', 'error');
      return;
    }
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>학생 생기부 프린트</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            html, body { 
              margin: 0; 
              padding: 0; 
              width: 100%; 
              height: 100vh;
              overflow: hidden;
            }
            img { 
              width: 100%; 
              height: 100vh;
              object-fit: contain;
              display: block; 
              page-break-inside: avoid;
              page-break-after: avoid;
              page-break-before: avoid;
            }
            @media print {
              html, body { 
                margin: 0 !important; 
                padding: 0 !important;
                width: 100% !important;
                height: 100vh !important;
                overflow: hidden !important;
              }
              img { 
                width: 100% !important; 
                height: 100vh !important;
                max-width: 100% !important;
                max-height: 100vh !important;
                object-fit: contain !important;
                page-break-inside: avoid !important;
                page-break-after: avoid !important;
                page-break-before: avoid !important;
              }
              @page { 
                margin: 0 !important; 
                size: A4 portrait !important;
              }
            }
          </style>
        </head>
        <body>
          <img src="${finalImage}" alt="학생 생기부" />
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `나는 ${persona.name}!`,
          text: generatedSaenggibu,
          url: window.location.href,
        });
      } catch (err) {
        //console.log('공유 취소됨');
      }
    } else {
      const url = window.location.href;
      await navigator.clipboard.writeText(url);
      showToast('링크를 복사했어요', 'success');
    }
  };

  // 페르소나 결과 화면
  if (currentView === 'persona') {
    return (
      <div className="student-profile-event-result-step">
        <div className="student-profile-event-result-container">
          <div className="student-profile-event-result-header">
            <div className="student-profile-event-celebration">
              <div className="student-profile-event-confetti">🎉</div>
              <div className="student-profile-event-confetti">✨</div>
              <div className="student-profile-event-confetti">🎊</div>
            </div>
            
            <h1 className="student-profile-event-result-title">
              분석 완료!<br/>
              <span className="student-profile-event-highlight">나의 학생 유형은</span>
            </h1>
          </div>

          <div className="student-profile-event-persona-result">
            <div 
              className="student-profile-event-persona-card"
              style={{ borderColor: persona.color }}
            >
              <div className="student-profile-event-persona-visual">
                <div 
                  className="student-profile-event-persona-circle"
                  style={{ background: `linear-gradient(135deg, ${persona.color}, ${persona.color}88)` }}
                >
                  <img 
                    src={`/images/${getPersonaImageNumber(persona)}.png`}
                    alt={persona.name}
                    className="student-profile-event-persona-image"
                  />
                </div>
              </div>
              
              <div className="student-profile-event-persona-content">
                <h2 className="student-profile-event-persona-name" style={{ color: persona.color }}>
                  {persona.name}
                </h2>
                <p className="student-profile-event-persona-subtitle">
                  {persona.subtitle}
                </p>
                
                <div className="student-profile-event-persona-keywords">
                  {persona.keywords.map((keyword, index) => (
                    <span 
                      key={index} 
                      className="student-profile-event-keyword-tag"
                      style={{ backgroundColor: `${persona.color}20`, color: persona.color }}
                    >
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="student-profile-event-next-step">
            <p className="student-profile-event-next-description">
              이제 <strong>왓퀴즈AI가 500자 내외의 개인화된 학생 생기부</strong>를 작성해드립니다!<br/>
              나의 강점 포인트를 선택해주세요.
            </p>
            <button 
              className="student-profile-event-continue-button"
              onClick={() => setCurrentView('emphasis')}
            >
              <span>생기부 작성하러 가기</span>
              <span className="student-profile-event-button-arrow">→</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 강조 포인트 선택 화면
  if (currentView === 'emphasis') {
    return (
      <div className="student-profile-event-result-step">
        <div className="student-profile-event-result-container">
          <div className="student-profile-event-emphasis-header">
            <button 
              className="student-profile-event-back-button"
              onClick={() => setCurrentView('persona')}
            >
              ← 이전으로
            </button>
            <h2 className="student-profile-event-emphasis-title">
              나의 강점 포인트를 선택하세요
            </h2>
            <p className="student-profile-event-emphasis-subtitle">
              최대 5개까지 선택 가능합니다 ({selectedEmphasis.length}/5)
            </p>
          </div>

          <div className="student-profile-event-emphasis-grid">
            {emphasisOptions.map((option) => (
              <div
                key={option.id}
                className={`student-profile-event-emphasis-option ${
                  selectedEmphasis.includes(option.id) ? 'selected' : ''
                }`}
                onClick={() => handleEmphasisToggle(option.id)}
              >
                <div className="student-profile-event-emphasis-label">
                  {option.label}
                </div>
                <div className="student-profile-event-emphasis-description">
                  {option.description}
                </div>
                {selectedEmphasis.includes(option.id) && (
                  <div className="student-profile-event-emphasis-check">✓</div>
                )}
              </div>
            ))}
          </div>

          <div className="student-profile-event-emphasis-actions">
            <button 
              className="student-profile-event-generate-button"
              onClick={generateSaenggibu}
              disabled={selectedEmphasis.length === 0}
            >
              <span>AI 생기부 생성하기</span>
              <span className="student-profile-event-button-arrow">→</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 생성 중 화면
  if (currentView === 'generating') {
    return (
      <div className="student-profile-event-result-step">
        <div className="student-profile-event-result-container">
          <div className="student-profile-event-generating-content">
            <div className="student-profile-event-generating-icon">
              <div className="student-profile-event-dolphin-thinking">🐬</div>
              <div className="student-profile-event-thinking-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
            <h2 className="student-profile-event-generating-title">
              왓퀴즈AI가 나만의<br/>
              <span className="student-profile-event-highlight">생기부를 작성하고 있습니다</span>
            </h2>
            <p className="student-profile-event-generating-subtitle">
              선택하신 강조 포인트와 설문 결과를 바탕으로<br/>
              500자 내외의 개인화된 학생 생기부를 생성 중입니다.
            </p>
            <div className="student-profile-event-generating-progress">
              <div className="student-profile-event-progress-bar">
                <div className="student-profile-event-progress-fill"></div>
              </div>
              <p className="student-profile-event-progress-text">잠시만 기다려주세요...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 최종 결과 화면
  return (
    <div className="student-profile-event-result-step student-profile-event-final-result">
      {/* 액션 버튼들 */}
      <div className="student-profile-event-final-actions no-print">
        <button 
          className="student-profile-event-print-button"
          onClick={handlePrint}
        >
          <span>프린트하기</span>
        </button>
        
        <button 
          className="student-profile-event-share-button"
          onClick={handleShare}
        >
          <span>결과 공유하기</span>
        </button>
        
        <button 
          className="student-profile-event-restart-button"
          onClick={onRestart}
        >
          <span>다시 해보기</span>
        </button>
      </div>

      {/* Canvas로 합성된 최종 이미지 */}
      {finalImage ? (
        <div className="student-profile-event-final-image-container">
          <img 
            src={finalImage}
            alt="학생 생기부 최종 결과"
            className="student-profile-event-final-image"
          />
        </div>
      ) : (
        <div className="student-profile-event-loading-image">
          <div className="student-profile-event-loading-spinner"></div>
          <p>프린트용 고품질 이미지 생성 중...</p>
        </div>
      )}

      {/* Canvas (숨김) */}
      <canvas 
        ref={canvasRef}
        style={{ display: 'none' }}
        className="student-profile-event-hidden-canvas"
      />
    </div>
  );
};

export default ResultStep;
